import { NavLink } from "react-router-dom";

export interface PageTab {
  /** Unique within the set; the path for link tabs. */
  key: string;
  label: string;
  /** A route: the tab is a link (a section's sub-pages). Leave out for in-page tabs (pass current and onSelect). */
  to?: string;
  /** Things waiting on you there, shown as an ochre count. */
  count?: number;
  /**
   * For link tabs: whether this tab is the current one, when the address alone can't tell (`/quotes` sits above
   * `/quotes/flows`). Left out, the address decides.
   */
  active?: boolean;
}

/**
 * PageTabs: a row of underlined tabs, seal underline on the current one. Two uses:
 * - a section's sub-pages (the layout renders these from lib/nav.ts, so screens need not): items with `to`;
 * - in-page views (Overview / History): items without `to`, plus `current` and `onSelect`.
 * Link tabs are a labelled navigation with aria-current="page"; in-page tabs are buttons with aria-pressed.
 * A detail page counts as its list: `/contacts/people/abc` marks "People" as current.
 */
export function PageTabs({ items, label, current, onSelect }: { items: PageTab[]; label: string; current?: string; onSelect?: (key: string) => void }) {
  if (items.length < 2) return null;
  return (
    <nav className="page-tabs" aria-label={label}>
      <ul>
        {items.map((t) => {
          const count = t.count && t.count > 0 ? (
            <span className="count" aria-hidden="true">
              {t.count}
            </span>
          ) : null;
          const name = t.count && t.count > 0 ? `${t.label}, ${t.count} waiting` : undefined;
          return (
            <li key={t.key}>
              {t.to ? (
                <NavLink
                  to={t.to}
                  className={({ isActive }) => `page-tab${(t.active ?? isActive) ? " is-active" : ""}`}
                  aria-current={t.active === undefined ? undefined : t.active ? "page" : false}
                  aria-label={name}
                >
                  <span>{t.label}</span>
                  {count}
                </NavLink>
              ) : (
                <button type="button" className={`page-tab${current === t.key ? " is-active" : ""}`} aria-pressed={current === t.key} aria-label={name} onClick={() => onSelect?.(t.key)}>
                  <span>{t.label}</span>
                  {count}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
